import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { IconUploadOutline, IconInformationOutline } from '@saucelabs/ds-icons';
import { Loader } from '@saucelabs/sl-component-loader';
import { Typography } from '@saucelabs/ds-typography';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';

import selectors from '../selectors';
import operations from '../operations';
import constants from '../../../constants';

const {
  APP_UPLOAD_STATUS,
  APP_STORAGE_LIFETIME_DAYS,
} = appStorageConstants;

type UploadBoxProps = {
  isDragActive: boolean;
};

const UploadBox: React.FC<UploadBoxProps> = ({ isDragActive }) => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { items: uploadingFiles } = useSelector(selectors.uploadSelector);

  const handleChooseFileClick = useCallback(() => {
    fileInputRef?.current?.click();
  }, []);

  const handleFileInputSelection = useCallback((event) => {
    const { target: { files } } = event;
    void dispatch(operations.uploadSelectedFilesAndRefreshGroups(Array.from(files)));

    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  }, [dispatch]);

  return (
    <div className="flex flex-row justify-center items-center">
      <div className={classNames('w-76 max-w-full min-h-5 m-2 mb-0 border border-dashed border-blue-700 rounded text-medium-emphasis', {
        'bg-blue-700': isDragActive,
        'bg-opacity-5': isDragActive,
      })}
      >
        <div className="flex justify-center m-1">
          <div className="flex items-center">
            <IconUploadOutline className="w-2 h-2 mr-1 text-blue-700 fill-current stroke-current" />
            <div className="text-center">
              <Typography component="span">{'Drag & drop or '}</Typography>
              <input
                ref={fileInputRef}
                accept={constants.ALLOWED_UPLOAD_FILE_TYPES_STRING}
                data-qa="app-management-upload-box-input"
                multiple
                onChange={handleFileInputSelection}
                style={{ display: 'none' }} // tailwind's `display: none` is overwritten by the main.css file
                type="file"
              />
              <button
                className="border-none bg-transparent p-0 text-blue-700 cursor-pointer hover:underline"
                data-qa="app-management-upload-box-trigger"
                onClick={handleChooseFileClick}
                type="button"
              >
                {'choose file'}
              </button>
              <Typography component="span">{` to upload an app (${constants.ALLOWED_UPLOAD_FILE_TYPES_STRING})`}</Typography>
            </div>
          </div>
        </div>

        {!!uploadingFiles.length && (
          <>
            <hr className="border-solid border-b-0 border border-gray-200 mx-2 my-0" />
            <div
              className="flex flex-col items-start text-high-emphasis mx-2 mt-1 mb-0"
              data-qa="app-management-upload-box-uploading-files-section"
            >
              <strong className="mb-1">Upload in progress</strong>
              {uploadingFiles.map((uploadingFile) => (
                <div
                  key={uploadingFile?.id}
                  className="mb-1 flex items-center font-normal break-words"
                >
                  <Loader
                    className="mr-1"
                    fontSize={1}
                    show
                  />
                  <p className="m-0 text-left">
                    <span>{`${uploadingFile?.name ?? ''} (${uploadingFile?.getPrettyFileSize() ?? ''}) • `}</span>
                    <span className="text-green-400">
                      {uploadingFile?.uploadStatus === APP_UPLOAD_STATUS.PROCESSING ?
                        'Processing' :
                        uploadingFile?.getPrettyProgressRounded()}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="mt-2.5 mr-2 text-medium-emphasis">
        <Tooltip
          placement="top"
          title={`Apps will be stored for ${APP_STORAGE_LIFETIME_DAYS} days and shared with your team`}
        >
          <span><IconInformationOutline className="w-2 h-2 fill-current" /></span>
        </Tooltip>
      </div>
    </div>
  );
};

export default UploadBox;
